/**
 * useCompactAfterDelete - Compacts chains horizontally after node deletion
 * 
 * When nodes are deleted, this hook detects if an entire chain was removed
 * (leaving a gap) and applies tetris compaction to shift remaining chains
 * left to fill the gap.
 * 
 * Positions ARE saved to the database (with skipRecordAction to avoid a separate
 * undo entry). The delete undo action is extended to restore pre-compaction layout.
 * savedPositions on task updates - without saving, the gap would reappear when
 * the user changes any task property (like status).
 * 
 * Animation: Triggers CSS transitions by calling onAnimationStart/onAnimationEnd
 * which adds the flowchart--animating-tidy class to enable smooth sliding.
 */

import { useRef, useCallback } from 'react';
import { compactChainsHorizontally } from './utils/tetrisCompaction';
import { findDisconnectedComponents } from '../layout';
import { buildDependencyGraph } from '../layout/graphBuilder';
import { calculateCampaignBounds } from './utils/campaignZoneUtils';
import { attachLayoutUndoToDelete } from './utils/attachLayoutUndoToDelete';

// Constants for multi-campaign zone spacing (should match CampaignZones and multiCampaignLayout)
const ZONE_PADDING = 50;
const INTER_CAMPAIGN_GAP = 30;

// Animation timing (matches CSS transition duration in node/base.css)
const ANIMATION_DURATION = 300;

/**
 * Ensure proper spacing between campaign zones after compaction
 * This recalculates campaign bounds and adjusts positions to maintain proper gaps
 * 
 * @param {Array} tasks - Current tasks
 * @param {Object} positions - Compacted positions
 * @param {Object} nodeDimensions - Node dimensions
 * @returns {Object} Adjusted positions with proper campaign spacing
 */
function ensureCampaignSpacing(tasks, positions, nodeDimensions) {
  const { bounds, campaignOrder } = calculateCampaignBounds(tasks, positions, nodeDimensions);
  
  // If only one campaign or no campaigns, no spacing needed
  if (campaignOrder.length <= 1) {
    return positions;
  }
  
  const adjustedPositions = { ...positions };
  
  // Calculate required offsets for each campaign to maintain proper spacing
  const campaignOffsets = {};
  campaignOrder.forEach(id => { campaignOffsets[id] = 0; });
  
  // Process campaigns from left to right, ensuring proper gaps
  for (let i = 1; i < campaignOrder.length; i++) {
    const leftCampaignId = campaignOrder[i - 1];
    const rightCampaignId = campaignOrder[i];
    
    const leftBounds = bounds[leftCampaignId];
    const rightBounds = bounds[rightCampaignId];
    
    // Calculate where the right campaign's zone should start
    // Left zone right edge + padding + gap + padding = right zone left edge
    const leftZoneRight = leftBounds.maxX + campaignOffsets[leftCampaignId] + ZONE_PADDING;
    const requiredRightZoneLeft = leftZoneRight + INTER_CAMPAIGN_GAP + ZONE_PADDING;
    
    // Current right zone left edge (after any previous adjustments)
    const currentRightZoneLeft = rightBounds.minX + campaignOffsets[rightCampaignId];
    
    // If there's overlap or not enough gap, push right campaign further right
    if (currentRightZoneLeft < requiredRightZoneLeft) {
      const adjustment = requiredRightZoneLeft - currentRightZoneLeft;
      // Apply this adjustment to this campaign and all campaigns to its right
      for (let j = i; j < campaignOrder.length; j++) {
        campaignOffsets[campaignOrder[j]] += adjustment;
      }
    }
  }
  
  // Apply offsets to all tasks
  tasks.forEach(task => {
    const campaignId = task.sourceCampaign?.id;
    if (campaignId && campaignOffsets[campaignId]) {
      const offset = campaignOffsets[campaignId];
      if (Math.abs(offset) > 0.5) {
        const currentPos = adjustedPositions[task.id];
        if (currentPos) {
          adjustedPositions[task.id] = {
            x: Math.round(currentPos.x + offset),
            y: currentPos.y,
          };
        }
      }
    }
  });
  
  return adjustedPositions;
}

/**
 * Hook to compact chains after deletion
 * 
 * @param {Object} params
 * @param {Array} params.tasks - Current tasks array
 * @param {Object} params.nodePositions - Current node positions
 * @param {Object} params.nodeDimensions - Node dimensions map
 * @param {Function} params.setCustomPositions - Function to update visual positions immediately
 * @param {Function} params.onPositionChangeBatch - Callback to save position changes to DB
 * @param {Function} params.wrapLastUndoAction - Extends the delete undo with layout restore
 * @param {Function} params.onApplyTaskPositions - Applies task x/y for undo/redo
 * @param {Function} params.onAnimationStart - Called before animation starts (to add CSS class)
 * @param {Function} params.onAnimationEnd - Called after animation completes (to remove CSS class)
 * @param {boolean} params.isMultiCampaignMode - Whether we're in multi-campaign view mode
 */
export function useCompactAfterDelete({
  tasks,
  nodePositions,
  nodeDimensions,
  setCustomPositions,
  onPositionChangeBatch,
  wrapLastUndoAction,
  onApplyTaskPositions,
  onAnimationStart,
  onAnimationEnd,
  isMultiCampaignMode = false,
}) {
  // Keep refs for values needed in after-delete handler
  const tasksRef = useRef(tasks);
  const nodePositionsRef = useRef(nodePositions);
  const nodeDimensionsRef = useRef(nodeDimensions);
  const setCustomPositionsRef = useRef(setCustomPositions);
  const onPositionChangeBatchRef = useRef(onPositionChangeBatch);
  const wrapLastUndoActionRef = useRef(wrapLastUndoAction);
  const onApplyTaskPositionsRef = useRef(onApplyTaskPositions);
  const onAnimationStartRef = useRef(onAnimationStart);
  const onAnimationEndRef = useRef(onAnimationEnd);
  const isMultiCampaignModeRef = useRef(isMultiCampaignMode);
  
  // Update refs on every render
  tasksRef.current = tasks;
  nodePositionsRef.current = nodePositions;
  nodeDimensionsRef.current = nodeDimensions;
  setCustomPositionsRef.current = setCustomPositions;
  onPositionChangeBatchRef.current = onPositionChangeBatch;
  wrapLastUndoActionRef.current = wrapLastUndoAction;
  onApplyTaskPositionsRef.current = onApplyTaskPositions;
  onAnimationStartRef.current = onAnimationStart;
  onAnimationEndRef.current = onAnimationEnd;
  isMultiCampaignModeRef.current = isMultiCampaignMode;
  
  /**
   * Handle compaction after nodes are deleted
   * Called after deletion is processed by React
   * 
   * Saves positions to DB (with skipRecordAction) to prevent the gap from
   * reappearing when the user changes task properties like status.
   */
  const handleCompactAfterDelete = useCallback((deletedIds) => {
    // Use setTimeout to ensure React has processed the deletion
    // and we have the updated tasks array
    setTimeout(() => {
      // Single-campaign delete is an animated ELK tidy. Horizontal tetris here
      // fights that pass and is what users see as a snap.
      if (!isMultiCampaignModeRef.current) {
        return;
      }

      const currentTasks = tasksRef.current;
      const positions = nodePositionsRef.current;
      const dimensions = nodeDimensionsRef.current;
      const setPositions = setCustomPositionsRef.current;
      const onBatchChange = onPositionChangeBatchRef.current;
      
      console.log('[CompactAfterDelete] Processing deletion, remaining tasks:', currentTasks.length);
      
      // If no tasks remain, nothing to compact
      if (currentTasks.length === 0) {
        console.log('[CompactAfterDelete] No tasks remaining, skipping compaction');
        return;
      }
      
      // Find current chains
      const graph = buildDependencyGraph(currentTasks);
      const components = findDisconnectedComponents(currentTasks, graph);
      
      console.log('[CompactAfterDelete] Current chains:', components.length);
      
      // If only one chain, nothing to compact
      if (components.length <= 1) {
        console.log('[CompactAfterDelete] Only one chain, skipping compaction');
        return;
      }
      
      // Apply tetris compaction to pull chains together
      let compactedPositions = compactChainsHorizontally(positions, currentTasks, dimensions);
      
      // In multi-campaign mode, ensure campaign zones maintain proper spacing after compaction
      if (isMultiCampaignModeRef.current) {
        compactedPositions = ensureCampaignSpacing(currentTasks, compactedPositions, dimensions);
      }
      
      // Check if any positions actually changed and build position changes list
      const positionChanges = [];
      const originalPositions = {};
      const compactedPositionsForUndo = {};
      
      currentTasks.forEach(task => {
        const oldPos = positions[task.id];
        const newPos = compactedPositions[task.id];
        
        if (oldPos && newPos && (oldPos.x !== newPos.x || oldPos.y !== newPos.y)) {
          originalPositions[task.id] = { x: oldPos.x, y: oldPos.y };
          compactedPositionsForUndo[task.id] = { x: newPos.x, y: newPos.y };
          positionChanges.push({
            taskId: task.id,
            position: newPos,
            oldPosition: { x: task.x, y: task.y },
          });
        }
      });
      
      if (positionChanges.length === 0) {
        console.log('[CompactAfterDelete] No position changes needed');
        return;
      }
      
      console.log('[CompactAfterDelete] Compacting', positionChanges.length, 'nodes');
      
      // Start animation (adds CSS class for transitions)
      onAnimationStartRef.current?.();
      
      // Use requestAnimationFrame to ensure animation class is applied before position change
      requestAnimationFrame(() => {
        // Update visual positions - CSS transition will animate the change
        if (setPositions) {
          setPositions(compactedPositions);
        }
        
        // Save positions to database after animation starts
        // Use skipRecordAction to avoid cluttering undo history
        setTimeout(() => {
          if (onBatchChange && positionChanges.length > 0) {
            onBatchChange(positionChanges, {
              batchType: 'COMPACT_AFTER_DELETE',
              batchDescription: 'Compact chains',
              skipRecordAction: true,
            });
          }

          attachLayoutUndoToDelete({
            wrapLastUndoAction: wrapLastUndoActionRef.current,
            applyTaskPositions: onApplyTaskPositionsRef.current,
            setCustomPositions: setPositions,
            originalPositions,
            compactedPositions: compactedPositionsForUndo,
          });
          
          // End animation after CSS transition completes
          setTimeout(() => {
            onAnimationEndRef.current?.();
          }, ANIMATION_DURATION);
        }, 50);
      });
    }, 100); // Wait for React to process deletion and update positions
  }, []);
  
  return {
    handleCompactAfterDelete,
  };
}

export default useCompactAfterDelete;
